import * as msal from '@azure/msal-browser';
import * as MicrosoftGraph from '@microsoft/microsoft-graph-client';
import { defineStore } from 'pinia';
import { useIoStore } from '@/store';
import { useActivityProjectStore } from '@/store/activityProjectStore';
import {
  checkAuthentication as msCheckAuth,
  createGraphClient,
  createMsalConfig,
  generateGUID,
  login as msLogin,
  htmlToPlainText,
} from '@/utils/helpers/msGraphHelpers';
import { getRedirectURI, getTenant } from '@/utils/helpers/urlManipulation';
import { shortenString } from '@/utils/helpers/various';

const subdomain = getTenant();
const tenantId = import.meta.env[`VITE_${subdomain}_MSPLANNER_TENANT_ID`];
const clientId = import.meta.env[`VITE_${subdomain}_MSPLANNER_CLIENT_ID`];
const config = createMsalConfig(clientId, tenantId, getRedirectURI());
const msalInstance = new msal.PublicClientApplication(config);

export type User = {
  id: string;
  displayName: string;
  mail: string;
};

export type Group = {
  id: string;
  displayName: string;
};

export type Planner = {
  id: string;
  title: string;
};

export type PlannerPlanWithGroup = {
  id: string;
  title: string;
  groupId: string;
  groupName?: string;
};

export type PlannerTask = {
  id: string;
  title: string;
  eTag: string;
};

export type PlannerImportTask = {
  id: string;
  title: string;
  percentComplete: number;
  dueDateTime: string | null;
  assignments?: Record<string, unknown>;
};

export type PlannerTaskCreateRequest = {
  planId: string;
  title: string;
  dueDateTime?: string;
  assignments: {
    [key: string]: {
      '@odata.type': '#microsoft.graph.plannerAssignment';
      orderHint: ' !';
    };
  };
};

export type PlannerTaskPatchRequest = {
  taskId: string;
  description: string;
  eTag: string;
};

export type ChecklistItem = {
  '@odata.type': 'microsoft.graph.plannerChecklistItem';
  title: string;
  isChecked: boolean;
};

export type PlannerStoreState = {
  scopes: Array<string>;
  user: User | null;
  groups: Array<Group>;
  groupUsers: Array<User>;
  planners: Array<Planner>;
  allPlans: Array<PlannerPlanWithGroup>;
  plannertasks: Array<PlannerTask>;
  plannerImportTasks: Array<PlannerImportTask>;
  isExporting: boolean;
  _graphClient: MicrosoftGraph.Client | null;
};

export const usePlannerStore = defineStore('plannerStore', {
  state: (): PlannerStoreState => ({
    scopes: [
      'User.Read',
      'GroupMember.Read.All',
      'User.ReadBasic.All',
      'Tasks.ReadWrite',
    ],
    user: null,
    groups: [],
    groupUsers: [],
    planners: [],
    allPlans: [],
    plannertasks: [],
    plannerImportTasks: [],
    isExporting: false,
    _graphClient: null,
  }),

  getters: {
    group(state) {
      return (id: string) => {
        const group = state.groups.find((group) => group.id === id);
        if (!group) {
          throw new Error('Group not found');
        }

        return group;
      };
    },

    getGroupIdForPlan(state) {
      return (planId: string): string | undefined => {
        const plan = state.allPlans.find((p) => p.id === planId);

        return plan?.groupId;
      };
    },

    plannersForGroup(state) {
      return (groupId: string): Array<{ id: string; title: string }> => {
        return state.allPlans
          .filter((plan) => plan.groupId === groupId)
          .map((plan) => ({ id: plan.id, title: plan.title }));
      };
    },

    // Lazy singleton: create Graph client once and reuse
    graphClient(state): MicrosoftGraph.Client {
      if (!state._graphClient) {
        state._graphClient = createGraphClient(msalInstance, this.scopes);
      }

      return state._graphClient as MicrosoftGraph.Client;
    },
  },

  actions: {
    async export(
      selectedGroup: string,
      selectedPhase: string,
      selectedPlanner: string,
      includeCriteriaAsChecklist: boolean = false,
    ) {
      let createRequest: PlannerTaskCreateRequest;
      await this.loadGroupUsersInfos(selectedGroup);
      const activities = useIoStore()
        .activitiesInPhase(selectedPhase)
        .filter((activity) => activity.state !== 'done')
        .sort((a, b) => {
          return b.deadline.localeCompare(a.deadline);
        });
      useActivityProjectStore().getProject(useIoStore().mapId);
      for (const activity of activities) {
        await useActivityProjectStore().loadActivity(
          useIoStore().mapId,
          activity.id,
        );
        const criteria = await useActivityProjectStore().loadCompletionCriteria(
          useIoStore().mapId,
          activity.id,
        );

        const activeCriteria = criteria.filter(
          (criterion) => criterion.state !== 'done',
        );

        // If we're NOT including criteria as checklist, create individual tasks
        if (!includeCriteriaAsChecklist) {
          let number = activeCriteria.length;
          for (const completionCriterion of criteria.reverse()) {
            if (completionCriterion.state === 'done') {
              continue;
            }
            const foundUser = this.groupUsers.find(
              (p) => p.mail === completionCriterion.responsible.user?.email,
            );
            const title = shortenString(
              `${$t('msPlannerIntegration.completionCriterionShort')}${
                activity.sequence
              }.${number} ${completionCriterion.title}`,
            );
            if (foundUser) {
              createRequest = {
                planId: selectedPlanner,
                title: title,
                dueDateTime: completionCriterion.manual_due_date
                  ? completionCriterion.current_deadline
                  : activity.deadline,
                assignments: {
                  [foundUser.id]: {
                    '@odata.type': '#microsoft.graph.plannerAssignment',
                    orderHint: ' !',
                  },
                },
              };
            } else {
              createRequest = {
                planId: selectedPlanner,
                title: title,
                dueDateTime: completionCriterion.manual_due_date
                  ? completionCriterion.current_deadline
                  : activity.deadline,
                assignments: {},
              };
            }
            const createdTask = await this.createTasksInPlanner(createRequest);
            const patchRequest: PlannerTaskPatchRequest = {
              taskId: createdTask.id,
              description: `https://${subdomain}.beeboard.eu/projects/${
                useIoStore().mapId
              }/activities/${activity.id}/completion-criteria/${
                completionCriterion.id
              }`,
              eTag: createdTask.eTag,
            };
            await this.patchTaskDetailsInPlanner(patchRequest);
            number -= 1;
          }
        }
        const responsibleEmail =
          typeof activity.responsibleUser === 'string'
            ? activity.responsibleUser
            : activity.responsibleUser?.email;

        const foundUser = this.groupUsers.find(
          (p) => p.mail === responsibleEmail,
        );
        const title = shortenString(
          `${$t('msPlannerIntegration.activityShort')}${activity.sequence} ${
            activity.title
          }`,
        );
        if (foundUser) {
          createRequest = {
            planId: selectedPlanner,
            title: title,
            dueDateTime: activity.deadline,
            assignments: {
              [foundUser.id]: {
                '@odata.type': '#microsoft.graph.plannerAssignment',
                orderHint: ' !',
              },
            },
          };
        } else {
          createRequest = {
            planId: selectedPlanner,
            title: title,
            dueDateTime: activity.deadline,
            assignments: {},
          };
        }
        const createdTask = await this.createTasksInPlanner(createRequest);
        const patchRequest: PlannerTaskPatchRequest = {
          taskId: createdTask.id,
          description: `https://${subdomain}.beeboard.eu/projects/${
            useIoStore().mapId
          }/activities/${activity.id}`,
          eTag: createdTask.eTag,
        };
        await this.patchTaskDetailsInPlanner(patchRequest);

        // If we're including criteria as checklist, add them now
        if (includeCriteriaAsChecklist && activeCriteria.length > 0) {
          const checklistItems = activeCriteria.map((criterion) => ({
            title: criterion.title,
            isChecked: false,
          }));
          await this.addChecklistItemsToTask(createdTask.id, checklistItems);
        }
      }
    },

    async exportSelectedActivities(
      selectedGroup: string,
      selectedActivityIds: string[],
      selectedPlanner: string,
      includeCriteriaAsChecklist: boolean = false,
    ) {
      let createRequest: PlannerTaskCreateRequest;
      await this.loadGroupUsersInfos(selectedGroup);

      // Get the selected activities from IoStore
      const ioStore = useIoStore();
      const activities = ioStore.activities
        .filter((activity) => selectedActivityIds.includes(activity.id))
        .filter((activity) => activity.state !== 'done')
        .sort((a, b) => {
          return b.deadline.localeCompare(a.deadline);
        });

      useActivityProjectStore().getProject(ioStore.mapId);

      for (const activity of activities) {
        await useActivityProjectStore().loadActivity(
          ioStore.mapId,
          activity.id,
        );
        const criteria = await useActivityProjectStore().loadCompletionCriteria(
          ioStore.mapId,
          activity.id,
        );

        const activeCriteria = criteria.filter(
          (criterion) => criterion.state !== 'done',
        );

        // If we're NOT including criteria as checklist, create individual tasks
        if (!includeCriteriaAsChecklist) {
          let number = activeCriteria.length;

          for (const completionCriterion of criteria.reverse()) {
            if (completionCriterion.state === 'done') {
              continue;
            }
            const foundUser = this.groupUsers.find(
              (p) => p.mail === completionCriterion.responsible.user?.email,
            );
            const title = shortenString(
              `${$t('msPlannerIntegration.completionCriterionShort')}${
                activity.sequence
              }.${number} ${completionCriterion.title}`,
            );
            if (foundUser) {
              createRequest = {
                planId: selectedPlanner,
                title: title,
                dueDateTime: completionCriterion.manual_due_date
                  ? completionCriterion.current_deadline
                  : activity.deadline,
                assignments: {
                  [foundUser.id]: {
                    '@odata.type': '#microsoft.graph.plannerAssignment',
                    orderHint: ' !',
                  },
                },
              };
            } else {
              createRequest = {
                planId: selectedPlanner,
                title: title,
                dueDateTime: completionCriterion.manual_due_date
                  ? completionCriterion.current_deadline
                  : activity.deadline,
                assignments: {},
              };
            }
            const createdTask = await this.createTasksInPlanner(createRequest);
            const patchRequest: PlannerTaskPatchRequest = {
              taskId: createdTask.id,
              description: `https://${subdomain}.beeboard.eu/projects/${
                ioStore.mapId
              }/activities/${activity.id}/completion-criteria/${
                completionCriterion.id
              }`,
              eTag: createdTask.eTag,
            };
            await this.patchTaskDetailsInPlanner(patchRequest);
            number -= 1;
          }
        }

        const responsibleEmail =
          typeof activity.responsibleUser === 'string'
            ? activity.responsibleUser
            : activity.responsibleUser?.email;

        const foundUser = this.groupUsers.find(
          (p) => p.mail === responsibleEmail,
        );
        const title = shortenString(
          `${$t('msPlannerIntegration.activityShort')}${activity.sequence} ${
            activity.title
          }`,
        );
        if (foundUser) {
          createRequest = {
            planId: selectedPlanner,
            title: title,
            dueDateTime: activity.deadline,
            assignments: {
              [foundUser.id]: {
                '@odata.type': '#microsoft.graph.plannerAssignment',
                orderHint: ' !',
              },
            },
          };
        } else {
          createRequest = {
            planId: selectedPlanner,
            title: title,
            dueDateTime: activity.deadline,
            assignments: {},
          };
        }
        const createdTask = await this.createTasksInPlanner(createRequest);
        const patchRequest: PlannerTaskPatchRequest = {
          taskId: createdTask.id,
          description: `https://${subdomain}.beeboard.eu/projects/${
            ioStore.mapId
          }/activities/${activity.id}`,
          eTag: createdTask.eTag,
        };
        await this.patchTaskDetailsInPlanner(patchRequest);

        // If we're including criteria as checklist, add them now
        if (includeCriteriaAsChecklist && activeCriteria.length > 0) {
          const checklistItems = activeCriteria.map((criterion) => ({
            title: criterion.title,
            isChecked: false,
          }));
          await this.addChecklistItemsToTask(createdTask.id, checklistItems);
        }
      }
    },

    async checkAuthentication() {
      return msCheckAuth(msalInstance, this.scopes);
    },

    async login() {
      await msLogin(msalInstance, this.scopes);
    },

    async loadUserInfos() {
      try {
        this.user = await this.graphClient.api('/me').get();
      } catch (error) {
        console.error(error);
      }
    },

    async loadGroupUsersInfos(groupId: string) {
      try {
        this.groupUsers = (
          await this.graphClient.api(`/groups/${groupId}/members`).get()
        ).value;
      } catch (error) {
        console.error(error);
      }
    },
    async loadGroups() {
      try {
        this.groups = (
          await this.graphClient
            .api(`/me/transitiveMemberOf/microsoft.graph.group`)
            .get()
        ).value;
      } catch (error) {
        console.error(error);
      }
    },
    async loadPlannersOfGroup(groupId: string) {
      try {
        this.planners = (
          await this.graphClient.api(`/groups/${groupId}/planner/plans`).get()
        ).value;
      } catch (error) {
        console.error(error);
      }
    },
    async loadAllPlans() {
      try {
        // Get all plans the user has access to in one API call
        const plans = (await this.graphClient.api('/me/planner/plans').get())
          .value;

        // Extract unique group IDs from plans (using container.containerId or fallback to owner)
        const groupIds = [
          ...new Set(
            plans.map(
              (plan: { container?: { containerId: string }; owner?: string }) =>
                plan.container?.containerId || plan.owner,
            ),
          ),
        ].filter(Boolean) as string[];

        // Batch fetch group names for all groups
        const groupNameMap = new Map<string, string>();
        if (groupIds.length > 0) {
          // Fetch groups in batches to avoid URL length limits
          const batchSize = 15;
          for (let i = 0; i < groupIds.length; i += batchSize) {
            const batch = groupIds.slice(i, i + batchSize);
            const filterQuery = batch.map((id) => `id eq '${id}'`).join(' or ');
            try {
              const groupsResponse = await this.graphClient
                .api('/groups')
                .filter(filterQuery)
                .select('id,displayName')
                .get();
              groupsResponse.value.forEach(
                (group: { id: string; displayName: string }) => {
                  groupNameMap.set(group.id, group.displayName);
                },
              );
            } catch (err) {
              console.error('Failed to fetch group names batch:', err);
            }
          }
        }

        // Map plans to include group information
        this.allPlans = plans.map(
          (plan: {
            id: string;
            title: string;
            container?: { containerId: string };
            owner?: string;
          }) => {
            const groupId = plan.container?.containerId || plan.owner || '';

            return {
              id: plan.id,
              title: plan.title,
              groupId,
              groupName: groupNameMap.get(groupId),
            };
          },
        );

        // Also populate the groups array with only groups that have planners
        this.groups = groupIds
          .filter((id) => groupNameMap.has(id))
          .map((id) => ({
            id,
            displayName: groupNameMap.get(id) || '',
          }));
      } catch (error) {
        console.error('Failed to load all plans:', error);
      }
    },
    async createTasksInPlanner(
      plannerTaskCreateRequest: PlannerTaskCreateRequest,
    ): Promise<PlannerTask> {
      try {
        const response = await this.graphClient
          .api(`/planner/tasks`)
          .post(plannerTaskCreateRequest);
        const etag = response['@odata.etag'].substring(
          response['@odata.etag'].indexOf('"'),
          response['@odata.etag'].lastIndexOf('"') + 1,
        );
        const plannerTask: PlannerTask = {
          id: response.id,
          title: response.title,
          eTag: etag,
        };

        return plannerTask;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    async patchTaskDetailsInPlanner(
      plannerTaskPatchRequest: PlannerTaskPatchRequest,
    ) {
      try {
        const taskDetails = await this.graphClient
          .api(`/planner/tasks/${plannerTaskPatchRequest.taskId}/details`)
          .get();
        const patchData = {
          description: plannerTaskPatchRequest.description,
        };
        const etag = taskDetails['@odata.etag'].substring(
          taskDetails['@odata.etag'].indexOf('"'),
          taskDetails['@odata.etag'].lastIndexOf('"') + 1,
        );
        await this.graphClient
          .api(`/planner/tasks/${plannerTaskPatchRequest.taskId}/details`)
          .header('If-Match', etag)
          .patch(patchData);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    async addChecklistItemsToTask(
      taskId: string,
      checklistItems: Array<{ title: string; isChecked?: boolean }>,
    ) {
      try {
        // First, get the current task details to get the etag
        const taskDetails = await this.graphClient
          .api(`/planner/tasks/${taskId}/details`)
          .get();

        // Generate checklist items with GUIDs
        const checklist: Record<string, ChecklistItem> = {};
        checklistItems.forEach((item) => {
          // Generate a GUID for each checklist item
          const guid = generateGUID();
          checklist[guid] = {
            '@odata.type': 'microsoft.graph.plannerChecklistItem',
            title: shortenString(item.title, 100),
            isChecked: item.isChecked || false,
          };
        });

        const patchData = {
          checklist: checklist,
        };

        const etag = taskDetails['@odata.etag'].substring(
          taskDetails['@odata.etag'].indexOf('"'),
          taskDetails['@odata.etag'].lastIndexOf('"') + 1,
        );

        await this.graphClient
          .api(`/planner/tasks/${taskId}/details`)
          .header('If-Match', etag)
          .patch(patchData);
      } catch (error) {
        console.error('Failed to add checklist items:', error);
        // Don't throw the error to prevent failing the entire export
      }
    },

    async exportExtractedTasksToPlanner(
      selectedGroup: string,
      selectedPlanner: string,
      tasks: Array<{
        title: string;
        description?: string | null;
        responsibleName?: string;
        responsibleEmail?: string;
        dueDate?: string | null;
      }>,
      context?: string,
    ) {
      await this.loadGroupUsersInfos(selectedGroup);

      for (const task of tasks) {
        const foundUser = this.groupUsers.find(
          (p) => p.mail === task.responsibleEmail,
        );

        const taskTitle = shortenString(task.title);
        const createRequest: PlannerTaskCreateRequest = {
          planId: selectedPlanner,
          title: taskTitle,
          dueDateTime: task.dueDate
            ? new Date(task.dueDate).toISOString()
            : undefined,
          assignments: foundUser
            ? {
                [foundUser.id]: {
                  '@odata.type': '#microsoft.graph.plannerAssignment',
                  orderHint: ' !',
                },
              }
            : {},
        };

        const createdTask = await this.createTasksInPlanner(createRequest);

        // Add context and responsible person to task details
        const descriptionParts = [
          `${$t('msTodoIntegration.taskBodyResponsible', { name: task.responsibleName || '', email: task.responsibleEmail || '' })}`,
        ];

        const descriptionText = htmlToPlainText(task.description);
        if (descriptionText) {
          descriptionParts.push(`\n\n${descriptionText}`);
        }

        if (context) {
          descriptionParts.push(
            `\n${$t('msTodoIntegration.taskBodyContext', { context })}`,
          );
        }

        const patchRequest: PlannerTaskPatchRequest = {
          taskId: createdTask.id,
          description: descriptionParts.join(''),
          eTag: createdTask.eTag,
        };

        await this.patchTaskDetailsInPlanner(patchRequest);
      }
    },

    async loadPlannerTasks(planId: string) {
      const response = await this.graphClient
        .api(`/planner/plans/${planId}/tasks`)
        .select('id,title,percentComplete,dueDateTime,assignments')
        .top(200)
        .get();
      this.plannerImportTasks = response.value || [];
    },

    async loadPlannerTaskDetails(
      taskId: string,
    ): Promise<{ description: string }> {
      const response = await this.graphClient
        .api(`/planner/tasks/${taskId}/details`)
        .select('description')
        .get();

      return { description: response.description || '' };
    },
  },
});
